import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { identityGateway } from '../services';
import { AuthShellLayout } from '../components/AuthShellLayout';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';

type CompletionState = 'checking' | 'succeeded' | 'failed';

/**
 * Reached via the unlock link UnlockRequestPage's flow (or LoginPage's Account Locked view) sends.
 * Calls `completeUnlock` on mount — there is no form here, just an automatic action and its outcome, the
 * same shape AcceptInvitationPage.tsx's own token-driven flow uses for its own no-form states.
 */
export function UnlockCompletePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [state, setState] = useState<CompletionState>(token ? 'checking' : 'failed');

  useEffect(() => {
    if (!token) {
      return;
    }
    let cancelled = false;
    identityGateway
      .completeUnlock(token)
      .then(() => {
        if (!cancelled) setState('succeeded');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state === 'checking') {
    return (
      <AuthShellLayout>
        <div className="auth-shell__form-slot">
          <p className="muted">Unlocking your account…</p>
        </div>
      </AuthShellLayout>
    );
  }

  if (state === 'failed') {
    return (
      <AuthShellLayout>
        <div className="auth-shell__form-slot">
          <h1>Unlock Link Invalid</h1>
          <InlineAlert variant="error" data-testid="unlock-complete-error-message">
            This link is invalid or has expired.
          </InlineAlert>
          <p className="auth-shell__secondary-action">
            <Link to="/unlock-request" className="auth-shell__inline-link">
              Request a new unlock link
            </Link>
          </p>
        </div>
      </AuthShellLayout>
    );
  }

  return (
    <AuthShellLayout>
      <div className="auth-shell__form-slot">
        <h1>Account Unlocked</h1>
        <InlineAlert variant="success" data-testid="unlock-complete-success-message">
          Your account has been unlocked — you can sign in now.
        </InlineAlert>
        <div className="auth-shell__submit-row">
          <Link to="/login">
            <Button type="button" className="auth-submit-button" data-testid="unlock-complete-go-to-login">
              Go to Sign In
            </Button>
          </Link>
        </div>
      </div>
    </AuthShellLayout>
  );
}
